import React, { useState } from 'react';
import {
  FaFileSignature, FaShieldAlt, FaLandmark, FaUsers,
  FaCheckCircle, FaChevronDown, FaHandshake, FaScroll,
  FaLock, FaUserShield
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
const EstatePlanning = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What happens if I pass away without an Estate Plan?",
      a: "Dying without a Will or Estate Plan is known as dying 'intestate.' When this happens, your assets are distributed according to the succession laws of your religion, which can lead to lengthy legal delays, frozen assets, and potential family disputes."
    },
    {
      q: "Is a Will enough, or do I need a Family Trust?",
      a: "While a Will is a crucial foundational document, it must go through a legal process called probate, which can be time-consuming and public. A Private Family Trust allows for the immediate, private transfer of assets, offers better control over how funds are used (e.g., for minor children), and helps protect assets from creditors."
    },
    {
      q: "When is the right time to start Estate Planning?",
      a: "Estate planning is not just for retirement. You should start planning as soon as you have dependents, acquire significant assets, or start a business. It should then be reviewed every few years or after major life events like marriage, the birth of a child, or a significant change in wealth."
    },
    {
      q: "Can I change my Will or Trust later?",
      a: "Yes. Your Estate Plan is a living document. You can amend or update your Will through a 'codicil' or draft a new one as your circumstances change. Revocable trusts can also be modified or dissolved during your lifetime."
    }
  ];

  return (
    <main className="estate-planning-page">
      <Helmet>
        {/* Basic SEO */}
        <title>Estate Planning Services in Hyderabad | Wills, Trusts & Wealth Protection | Lotoswealth</title>

        <meta
          name="description"
          content="Lotoswealth offers professional Estate Planning Services in Hyderabad, helping individuals and families protect, preserve, and transfer their wealth through wills, trusts, succession planning, inheritance planning, wealth protection, and legacy planning."
        />

        <meta
          name="keywords"
          content="Estate Planning Services Hyderabad, Estate Planning Hyderabad, Estate Planning Services in Hyderabad, Wills and Trusts Hyderabad, Wealth Protection Hyderabad, Wealth Transfer Hyderabad, Succession Planning Hyderabad, Inheritance Planning Hyderabad, Legacy Planning Hyderabad, Family Wealth Planning Hyderabad, Trust Planning Hyderabad, Will Writing Hyderabad, Asset Protection Hyderabad, Financial Planning Hyderabad, Wealth Management Hyderabad, Investment Advisor Hyderabad, Financial Advisor Hyderabad, Tax Planning Hyderabad, Retirement Planning Hyderabad, Family Office Services Hyderabad, Lotoswealth"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lotoswealth" />
        <meta name="language" content="English" />

        <link rel="canonical" href="https://www.lotoswealth.com/estate-planning" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lotoswealth" />
        <meta property="og:url" content="https://www.lotoswealth.com/estate-planning" />
        <meta
          property="og:title"
          content="Estate Planning Services in Hyderabad | Lotoswealth"
        />
        <meta
          property="og:description"
          content="Protect your family's future with Estate Planning Services in Hyderabad. Lotoswealth provides wills, trusts, succession planning, inheritance planning, and wealth protection strategies."
        />
        <meta
          property="og:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Estate Planning Services in Hyderabad | Lotoswealth"
        />
        <meta
          name="twitter:description"
          content="Secure your legacy with Estate Planning Services in Hyderabad. Expert guidance on wills, trusts, succession planning, inheritance planning, and wealth protection."
        />
        <meta
          name="twitter:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />
      </Helmet>
      {/* =========================================
          1. HERO SECTION (With Ticker & Slow Zoom)
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '100vh', minHeight: '800px', overflow: 'hidden' }}>

        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 animate-slow-zoom">
          <img
            src="/images/estate-planning.jpg"
            alt="Estate & Succession Planning"
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text (Perfectly Centered) --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            Estate & Succession Planning
          </h1>
          <h2 className="animate-slide-up delay-2 fw-bold text-white mb-4" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Secure Your Legacy for Generations to Come
          </h2>
          <p className="hero-description animate-slide-up delay-3 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Welcome to Lotoswealth. You have spent a lifetime building your wealth—now it is time to protect it. Our Estate and Succession Planning services ensure that your assets are seamlessly transferred to your chosen beneficiaries with minimal legal hurdles, tax liabilities, or family disputes.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-4 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            A well-structured estate plan goes far beyond drafting a simple Will. It involves creating a comprehensive legal and financial framework that safeguards your family's future, protects your business continuity, and honors your final wishes exactly as you intended.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-5 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            From establishing Private Family Trusts to structuring complex corporate successions, our expert advisors handle the intricacies of wealth transfer with absolute confidentiality, empathy, and legal precision.
          </p>
        </div>

        {/* --- Scrolling Stock Ticker --- */}
        <div className="stock-ticker position-absolute bottom-0 w-100 z-3 bg-dark text-white border-top border-bottom border-secondary py-2" style={{ opacity: 0.9 }}>
          <div className="ticker-track">
            {/* First Set */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>

            {/* Duplicated Set for Infinite Scrolling */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
          </div>
        </div>
      </div>

      {/* =========================================
          2. WHY ESTATE PLANNING MATTERS (Soft Teal Theme)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                Why Estate Planning is Essential
              </h2>
              <p className="text-muted line-height-lg">
                Without a clear estate plan, the wealth you have spent a lifetime accumulating can become a source of stress and conflict for your loved ones. Intestate succession laws are complex, often resulting in frozen bank accounts, locked real estate, and prolonged legal battles.
              </p>
              <p className="text-muted line-height-lg">
                Proactive estate planning ensures that your spouse is financially secure, your children's futures are funded, and your business operations continue smoothly, all while maintaining strict privacy and minimizing the tax burden on your heirs.
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">The Power of Preparedness</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Avoid Disputes:</strong> Prevent family conflicts over asset distribution.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Bypass Probate:</strong> Utilize trusts for faster, private wealth transfer.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Protect Minors:</strong> Appoint trusted guardians and manage funds for children.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Business Continuity:</strong> Ensure smooth succession for your enterprise.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. OUR ESTATE SERVICES (Crisp White)
      ========================================= */}
      <section className="py-5 bg-white" style={{ backgroundImage: "url('/images/lotoswealth-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>

        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">📜 Our Estate Planning Services</h2>
            <p className="text-white">Comprehensive, legally sound solutions for total peace of mind.</p>
          </div>

          <div className="row g-4">

            <div className="col-lg-4 col-md-6 animate-slide-up delay-1">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaFileSignature size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Will Drafting & Registration</h5>
                <p className="text-muted small">We help you draft legally binding, unambiguous Wills that clearly outline your asset distribution, avoiding the pitfalls of generic templates.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-2">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaLandmark size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Private Family Trusts</h5>
                <p className="text-muted small">Establish Revocable or Irrevocable Trusts to protect assets from creditors, bypass the probate process, and provide controlled financial support to beneficiaries.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-3">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaHandshake size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Business Succession Planning</h5>
                <p className="text-muted small">Secure the future of your company with clear succession strategies, buy-sell agreements, and leadership transition plans for smooth continuity.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-4">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaScroll size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Power of Attorney (PoA)</h5>
                <p className="text-muted small">Draft General or Specific Powers of Attorney and Healthcare Directives to ensure trusted individuals can make decisions on your behalf if you become incapacitated.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-5">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaShieldAlt size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Tax-Efficient Wealth Transfer</h5>
                <p className="text-muted small">Structure your asset transfer strategy to minimize the impact of potential estate taxes, capital gains, and stamp duties on your heirs.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-6">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaUserShield size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Executorship & Trusteeship</h5>
                <p className="text-muted small">Act as an impartial, professional Executor or Corporate Trustee to administer your estate objectively and precisely according to your wishes.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          4. OUR 5-STEP PLANNING PROCESS (Dark Slate)
      ========================================= */}
      <section className="py-5 text-white" style={{ backgroundColor: '#0a192f' }}>
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🔄 How We Secure Your Legacy</h2>
            <p className="text-light opacity-75">A structured, sensitive, and strictly confidential process.</p>
          </div>

          <div className="row g-4">

            <div className="col-md-6 col-lg-4 animate-slide-up delay-1">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 1 – Asset Discovery</h5>
                <p className="small text-muted mb-0">We consolidate and review your global assets, including real estate, equity, businesses, bank accounts, and digital assets.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-2">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 2 – Objective Setting</h5>
                <p className="small text-muted mb-0">We sit down to understand your family dynamics, philanthropic goals, and exact wishes for wealth distribution.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-3">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 3 – Legal Structuring</h5>
                <p className="small text-muted mb-0">Our legal and financial experts collaborate to design the optimal structure—be it a simple Will or a complex Family Trust.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-6 animate-slide-up delay-4">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 4 – Drafting & Execution</h5>
                <p className="small text-muted mb-0">We draft all necessary documents, assist with formal registration, and ensure all paperwork is legally watertight and compliant.</p>
              </div>
            </div>

            <div className="col-md-12 col-lg-6 animate-slide-up delay-5">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 5 – Regular Review</h5>
                <p className="small text-muted mb-0">An estate plan is not a one-time event. We periodically review and update your plan to reflect new asset acquisitions or life changes.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          5. WHO WE SERVE & WHY CHOOSE US (Soft Blue-Gray)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#eef2f5' }}>
        <div className="container py-4">
          <div className="row g-5">

            {/* Target Audience */}
            <div className="col-lg-5 animate-slide-up delay-1">
              <h3 className="fw-bold mb-4 text-dark">👥 Who Needs Estate Planning?</h3>
              <p className="text-muted small mb-4">Proper succession planning is highly recommended for:</p>
              <div className="d-flex flex-wrap gap-2">
                {[
                  "High Net Worth Individuals (HNIs)", "Business Owners & Promoters",
                  "Parents with Minor Children", "Real Estate Investors",
                  "Families with Special Needs Dependents", "NRIs with Indian Assets",
                  "Retirees & Senior Citizens", "Blended Families"
                ].map((item, idx) => (
                  <span key={idx} className="badge bg-white text-dark border p-2 shadow-sm fs-6">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Why Choose Lotoswealth */}
            <div className="col-lg-7 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100">
                <h3 className="fw-bold mb-3 text-dark">🏆 Why Trust Lotoswealth?</h3>
                <p className="text-muted small mb-4">Estate planning requires immense sensitivity, absolute confidentiality, and deep legal expertise. We offer:</p>
                <div className="row g-3">
                  {[
                    "Integrated Legal & Financial Advice", "Absolute Confidentiality & Privacy",
                    "Customized Trust Structuring", "Smooth Cross-Border Inheritance",
                    "Expertise in Succession Laws", "Impartial Executor Services",
                    "Empathetic Family Coordination", "Seamless Coordination with Tax Experts"
                  ].map((item, idx) => (
                    <div key={idx} className="col-md-6 d-flex align-items-center">
                      <FaCheckCircle className="text-success me-2 flex-shrink-0" />
                      <span className="small text-muted fw-bold">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          6. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding Wills, Trusts & Estate Planning</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          7. FINAL CTA SECTION (Deep Navy)
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Secure Your Family's Future Today</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            It is never too early to start planning your legacy. Ensure your wealth is protected and distributed exactly according to your wishes.
          </p>
          <button className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3">
            Schedule a Confidential Consultation
          </button>
        </div>
      </section>

    </main>
  );
};

export default EstatePlanning;